import type { FirebaseOptions } from "firebase/app";

export type FirebaseBrowserEnv = Partial<Record<
  | "VITE_FIREBASE_API_KEY"
  | "VITE_FIREBASE_AUTH_DOMAIN"
  | "VITE_FIREBASE_PROJECT_ID"
  | "VITE_FIREBASE_APP_ID",
  string
>>;

export type FirebaseBrowserConfigResult =
  | { ok: true; config: FirebaseOptions & { apiKey: string; authDomain: string; projectId: string } }
  | { ok: false; missing: Array<keyof FirebaseBrowserEnv> };

export type GoogleSignInResult = {
  idToken: string;
  email: string | null;
};

export class GoogleRedirectStartedError extends Error {
  code = "auth/redirect-started";

  constructor() {
    super("Google sign-in is continuing in this browser.");
    this.name = "GoogleRedirectStartedError";
  }
}

type LocalAuthTestHook = {
  firebaseConfig?: FirebaseOptions & { apiKey: string; authDomain: string; projectId: string };
  googleSignInResult?: GoogleSignInResult;
};

declare global {
  interface Window {
    __rlsLocalAuthTestHook?: LocalAuthTestHook;
  }
}

const requiredFirebaseEnvKeys = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID"
] as const;

export function getFirebaseBrowserConfig(env: FirebaseBrowserEnv = import.meta.env): FirebaseBrowserConfigResult {
  const localTestConfig = getLocalAuthTestHook()?.firebaseConfig;
  if (localTestConfig) {
    return {
      ok: true,
      config: localTestConfig
    };
  }

  const missing = requiredFirebaseEnvKeys.filter((key) => !normalizeEnvValue(env[key]));
  if (missing.length > 0) return { ok: false, missing: [...missing] };

  const appId = normalizeEnvValue(env.VITE_FIREBASE_APP_ID);

  return {
    ok: true,
    config: {
      apiKey: normalizeEnvValue(env.VITE_FIREBASE_API_KEY),
      authDomain: normalizeEnvValue(env.VITE_FIREBASE_AUTH_DOMAIN),
      projectId: normalizeEnvValue(env.VITE_FIREBASE_PROJECT_ID),
      ...(appId ? { appId } : {})
    }
  };
}

export async function signInWithGoogleAndGetIdToken(config: FirebaseOptions) {
  return (await signInWithGoogle(config)).idToken;
}

export async function startGoogleRedirectSignIn(config: FirebaseOptions): Promise<never> {
  const { auth, provider, getRedirectResult, signInWithRedirect } = await loadGoogleAuth(config);

  const redirectSignIn = await credentialToSignInResult(await getRedirectResult(auth));
  if (redirectSignIn) {
    throw new Error("Google sign-in already finished.");
  }

  await signInWithRedirect(auth, provider);
  throw new GoogleRedirectStartedError();
}

export async function signInWithGoogle(config: FirebaseOptions): Promise<GoogleSignInResult> {
  const localTestSignIn = getLocalAuthTestHook()?.googleSignInResult;
  if (localTestSignIn?.idToken) return localTestSignIn;

  const { auth, provider, getRedirectResult, signInWithPopup, signInWithRedirect } = await loadGoogleAuth(config);

  const redirectSignIn = await credentialToSignInResult(await getRedirectResult(auth));
  if (redirectSignIn) return redirectSignIn;

  try {
    const popupSignIn = await credentialToSignInResult(await signInWithPopup(auth, provider));
    if (!popupSignIn) throw new Error("Google sign-in did not finish.");
    return popupSignIn;
  } catch (error) {
    if (!isPopupBlockedError(error)) throw error;
    await signInWithRedirect(auth, provider);
    throw new GoogleRedirectStartedError();
  }
}

export async function getGoogleRedirectSignInResult(config: FirebaseOptions): Promise<GoogleSignInResult | null> {
  const localTestSignIn = getLocalAuthTestHook()?.googleSignInResult;
  if (localTestSignIn?.idToken) return null;

  const { auth, getRedirectResult } = await loadGoogleAuth(config);
  return credentialToSignInResult(await getRedirectResult(auth));
}

async function loadGoogleAuth(config: FirebaseOptions) {
  const [
    { getApps, initializeApp },
    { getAuth, GoogleAuthProvider, getRedirectResult, signInWithPopup, signInWithRedirect }
  ] = await Promise.all([
    import("firebase/app"),
    import("firebase/auth")
  ]);

  const app = getApps().length > 0 ? getApps()[0] : initializeApp(config);
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const auth = getAuth(app);

  return {
    auth,
    provider,
    getRedirectResult,
    signInWithPopup,
    signInWithRedirect
  };
}

async function credentialToSignInResult(credential: { user?: { getIdToken: () => Promise<string>; email: string | null } } | null) {
  if (!credential?.user) return null;
  return {
    idToken: await credential.user.getIdToken(),
    email: credential.user.email
  };
}

function isPopupBlockedError(error: unknown) {
  const code = typeof error === "object" && error && "code" in error
    ? String((error as { code?: unknown }).code || "")
    : "";
  return code.includes("popup-blocked") || code.includes("popup_closed_by_browser");
}

function normalizeEnvValue(value: string | undefined) {
  return String(value || "").trim();
}

function getLocalAuthTestHook() {
  if (typeof window === "undefined") return null;
  if (!["localhost", "127.0.0.1", "::1"].includes(window.location.hostname)) return null;
  return window.__rlsLocalAuthTestHook || null;
}
